<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import BackspaceIcon from "@/components/Icons/BackspaceIcon.vue";

type Props = {
  keyboardKey: string;
  isCorrect: boolean;
  isPresent: boolean;
  isWrong: boolean;
};

const props = defineProps<Props>();
const emit = defineEmits<{ (e: "click", key: string): void }>();

const isTouching = ref(false);

let keydownHandler: ((e: KeyboardEvent) => void) | null = null;

onMounted(() => {
  keydownHandler = (e: KeyboardEvent) => {
    if (e.key.toLowerCase() === props.keyboardKey) {
      clicked();
    }
  };
  window.addEventListener("keydown", keydownHandler);
});

onUnmounted(() => {
  if (keydownHandler) {
    window.removeEventListener("keydown", keydownHandler);
  }
});

const timeOut = ref<ReturnType<typeof setTimeout> | null>(null);

function setClickEffect(): void {
  if (timeOut.value) clearTimeout(timeOut.value);
  isTouching.value = true;
  timeOut.value = setTimeout(() => {
    isTouching.value = false;
    timeOut.value = null;
  }, 300);
}

function clicked(): void {
  setClickEffect();
  emit("click", props.keyboardKey);
}

function handleTouchStart(event: TouchEvent): void {
  event.preventDefault();
}

function handleTouchEnd(event: TouchEvent): void {
  event.preventDefault();
  clicked();
}
</script>
<template>
  <div
    :class="[
      isTouching ? 'scale-105 border border-black-dark dark:border-white' : '',
      isCorrect
        ? 'bg-green-600 fill-white text-white'
        : isPresent
          ? 'bg-yellow-600 fill-white text-white'
          : isWrong
            ? 'bg-gray-500 text-white'
            : 'bg-gray-200',
      ['enter', 'backspace'].includes(keyboardKey) ? 'w-12' : 'w-8',
    ]"
    class="text-md flex h-14 cursor-pointer touch-manipulation select-none justify-center rounded py-4 align-middle font-medium capitalize text-black-dark transition-all duration-75"
    @click="clicked"
    @touchstart="handleTouchStart"
    @touchend="handleTouchEnd"
  >
    <BackspaceIcon v-if="keyboardKey === 'backspace'" />
    <template v-else> {{ keyboardKey }} </template>
  </div>
</template>
